'use client'

import { Suspense, useEffect, useState } from 'react'
import Link from 'next/link'
import { Skeleton } from '@/components/ui/skeleton'
import type {
  UserGamificationSummary,
  PhishTrainerUser,
  KnowledgeProfile,
  Round,
  TierKey,
} from '@/lib/phishtrainer/types'
import { TIER_DEFS } from '@/lib/phishtrainer/types'
import { toast } from 'sonner'
import { TierHeroBand } from '@/components/phishtrainer/me/tier-hero-band'
import { NextRoundCTA } from '@/components/phishtrainer/me/next-round-cta'
import { AtAGlanceCard } from '@/components/phishtrainer/me/at-a-glance-card'
import { LeaderboardCard, type LeaderboardRow } from '@/components/phishtrainer/me/leaderboard-card'
import { AchievementsRow, type AchievementItem } from '@/components/phishtrainer/me/achievements-row'
import { PillarMasteryStrip, type PillarItem } from '@/components/phishtrainer/me/pillar-mastery-strip'
import { RecentRoundsCard, type RecentRound } from '@/components/phishtrainer/me/recent-rounds-card'
import { FocusAndQuestsCard } from '@/components/phishtrainer/me/focus-and-quests-card'
import { TierUpCelebration } from '@/components/phishtrainer/me/tier-up-celebration'
import { StreakAtRiskBanner } from '@/components/phishtrainer/me/streak-at-risk-banner'
import { WeeklyQuestsCard } from '@/components/phishtrainer/me/weekly-quests-card'
import { ModePreferenceCard } from '@/components/phishtrainer/me/mode-preference-card'
import { AnonMerge } from '@/components/phishtrainer/me/anon-merge'
import { usePlayDashboard, usePlayRoundHistory } from '@/components/phishtrainer/hooks/use-play-dashboard'
import { usePlayKnowledge } from '@/components/phishtrainer/hooks/use-play-knowledge'
import { usePlayLeaderboard } from '@/components/phishtrainer/hooks/use-play-leaderboard'
import { useTranslation } from '@/lib/i18n'
import { formatLearnerDate } from '@/components/user-portal/learner-i18n'
import { usePlayerAuth } from './_auth/player-auth-provider'

type Translate = (key: string, params?: Record<string, string | number>) => string

// The P.H.I.S.H. letters are the framework's own and stay as they are; the
// pillar names are shown in the learner's language (play.pillars.*).
const PILLAR_LABELS: Record<string, { letter: string; color: string }> = {
  PF1: { letter: 'P', color: '#ef4444' },
  PF2: { letter: 'H', color: '#f59e0b' },
  PF3: { letter: 'I', color: '#a855f7' },
  PF4: { letter: 'S', color: '#3b82f6' },
  PF5: { letter: 'H', color: '#10b981' },
}

export default function PlayerHome() {
  return (
    <Suspense fallback={<DashboardSkeleton />}>
      <PlayerHomeInner />
    </Suspense>
  )
}

function PlayerHomeInner() {
  const { user, loading: authLoading } = usePlayerAuth()
  const { t, locale } = useTranslation()

  // Migrated onto the module data layer. This page ran ONE effect issuing four
  // parallel fetches, each with its own .catch-to-empty, feeding six pieces of
  // local state behind a single shared loading flag — so a slow leaderboard held
  // the whole page on a skeleton. Now each resource resolves independently and
  // the knowledge query is the SAME cache entry /play/me uses, so moving between
  // the two pages no longer refetches it.
  const userId = user?.id

  const {
    data: summary,
    isPending: summaryPending,
    isError: summaryError,
  } = usePlayDashboard(userId)
  const { data: rounds = [], isPending: roundsPending } = usePlayRoundHistory(userId, 10)
  const { data: knowledge = null } = usePlayKnowledge(userId)
  const { data: leaderboard } = usePlayLeaderboard(50, !!userId)

  const leaderboardTop = leaderboard?.top ?? []
  const leaderboardPinned = leaderboard?.pinned ?? null
  // Gate on the two queries the first paint actually needs; the leaderboard and
  // knowledge fill in behind their own cards.
  const loading = !!userId && (summaryPending || roundsPending)

  const [celebrateTier, setCelebrateTier] = useState<TierKey | null>(null)
  const [streakBannerDismissed, setStreakBannerDismissed] = useState(false)

  // ── Post-load side effects ────────────────────────────────────────────────
  // These used to run inline at the tail of the fetch effect. They are keyed on
  // the DATA now, so they fire when a query resolves (including from cache) and
  // never on a bare re-render.

  // Tier-up detection (localStorage-backed).
  useEffect(() => {
    const id = userId
    const tier = summary?.currentTier
    if (!id || !tier) return
    try {
      const lastKnownTier = localStorage.getItem(`pt-me-last-tier-${id}`) as TierKey | null
      const tierOrder: TierKey[] = ['bronze', 'silver', 'gold', 'diamond', 'rockstar']
      if (lastKnownTier && tierOrder.indexOf(tier) > tierOrder.indexOf(lastKnownTier)) {
        setCelebrateTier(tier)
      }
      localStorage.setItem(`pt-me-last-tier-${id}`, tier)
    } catch {}
  }, [userId, summary?.currentTier])

  // Social-comparison toasts (diff the previous leaderboard snapshot).
  useEffect(() => {
    const id = userId
    if (!id || !leaderboard) return
    try {
      const key = `pt-me-last-lb-${id}`
      const prevRaw = localStorage.getItem(key)
      const nextTop = leaderboard.top
      const nextMe =
        (leaderboard.pinned?.userId === id
          ? leaderboard.pinned
          : nextTop.find((r) => r.userId === id)) ?? null
      if (prevRaw && nextMe && typeof nextMe.rank === 'number') {
        const prev: {
          top: Array<{ userId: string; name: string; rank: number | null }>
          me: { rank: number | null } | null
        } = JSON.parse(prevRaw)
        const prevRank = prev.me?.rank
        const nextRank = nextMe.rank
        if (typeof prevRank === 'number' && prevRank > nextRank) {
          const passed = prev.top.filter(
            (pRow) =>
              pRow.userId !== id &&
              typeof pRow.rank === 'number' &&
              pRow.rank < prevRank &&
              pRow.rank >= nextRank,
          )
          for (const pRow of passed.slice(0, 2)) {
            toast(t('play.home.passedToast', { name: pRow.name }), {
              description: t('play.home.passedToastBody'),
            })
          }
        }
      }
      localStorage.setItem(
        key,
        JSON.stringify({
          top: nextTop.map((r) => ({ userId: r.userId, name: r.name, rank: r.rank })),
          me: nextMe ? { rank: nextMe.rank } : null,
          at: Date.now(),
        }),
      )
    } catch {}
    // A language switch re-runs this against the snapshot it just saved, so it
    // raises nothing.
  }, [userId, leaderboard, t])

  if (!authLoading && !loading && user && summaryError) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center space-y-4">
        <h1 className="text-2xl font-bold tracking-tight">{t('play.home.loadFailed')}</h1>
        <p className="text-muted-foreground">
          {t('play.home.loadFailedBody')}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center rounded-lg border bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t('play.home.tryAgain')}
        </button>
      </div>
    )
  }

  if (authLoading || loading || !user || !summary) return <DashboardSkeleton />

  const fullName = user.name || user.id
  const firstName = fullName.split(' ')[0] || 'there'

  // Derive weakest pillar from knowledge (fallback to PF3)
  const weakestPillar = deriveWeakestPillar(knowledge, t)

  // Next-round meta
  // The CTA deliberately does NOT pre-select an attack.
  //
  // This card used to fetch `GET /api/phishtrainer/rounds/next` just to render
  // its difficulty + attackId. That endpoint is a WRITE: getNext unconditionally
  // inserts a phishtrainer_rounds row (rounds.service.ts:538). So merely OPENING
  // the dashboard created an InProgress round the learner never played, and the
  // engagement report classifies an unfinished round older than 30 minutes as
  // "abandoned" — every per-user and per-email abandonment rate an admin sees
  // was inflated by dashboard views rather than by real abandonment.
  //
  // Selection now happens where it belongs: when the learner actually starts,
  // /play/game calls rounds/next itself. The focus pillar still comes from the
  // knowledge profile this page already loads, so the CTA loses nothing the
  // learner relies on — only the pre-peeked difficulty band, which is not worth
  // corrupting the module's abandonment metrics for.
  const playHref = '/play/game'
  const singlePlayHref = '/play/game?single=1'
  const nextRoundProps = {
    difficulty: 'Medium',
    expectedStars: 2,
    pillarFocusCode: weakestPillar.code,
    durationMin: 2,
    playHref,
  }

  // Leaderboard scopes — for v1 we only have company from the API call; team/dept returned empty.
  // We show the same "company" list under all three tabs for now; team/dept scoping would need
  // additional fetches (out of scope for this task, can be added later).
  const lbRows: LeaderboardRow[] = leaderboardTop.map((r: any) => ({
    rank: r.rank,
    userId: r.userId,
    name: r.name,
    avatar: initials(r.name),
    currentStars: r.currentStars,
    isMe: r.userId === user.id,
    delta: undefined,
    department: r.department ?? null,
  }))
  if (leaderboardPinned) {
    lbRows.push({
      rank: (leaderboardPinned as any).rank,
      userId: (leaderboardPinned as any).userId,
      name: (leaderboardPinned as any).name,
      avatar: initials((leaderboardPinned as any).name),
      currentStars: (leaderboardPinned as any).currentStars,
      isMe: true,
    })
  }
  // Leaderboard scoping. Recipients carry a `department` (no `team` field
  // exists), so we offer Company + Department — never a fake "Team" tab. The
  // Department board is the viewer's own department, re-ranked 1..N; a learner
  // with no department lands in the "No department" bucket. We only surface the
  // Department tab when the workspace actually spans MORE THAN ONE department —
  // otherwise it would just duplicate the company board.
  // The Department tab groups the whole board BY department name (the card does
  // the grouping), so pass all rows — not just the viewer's department. Only
  // surface it when the workspace actually spans >1 department.
  const deptDiversity = new Set(lbRows.map((r) => r.department ?? '')).size > 1
  const leaderboardScopes: { company: LeaderboardRow[]; dept?: LeaderboardRow[] } =
    deptDiversity ? { company: lbRows, dept: lbRows } : { company: lbRows }

  // Pie/donut: reported/clicked/missed from round history
  const progress = computeProgressBuckets(rounds)

  // Achievements
  const achievementItems: AchievementItem[] = [
    ...summary.achievements.unlocked,
    ...summary.achievements.inProgress,
    ...summary.achievements.locked,
  ]
    .slice(0, 6)
    .map((a) => ({
      id: a.def.id,
      name: a.def.name,
      desc: a.def.description,
      state: a.state,
      color: a.def.colorHex,
      iconKey: a.def.iconKey as AchievementItem['iconKey'],
      progress: a.progress,
      total: a.def.target,
      unlockedAgo: a.unlockedAt ? timeAgo(a.unlockedAt, t) : undefined,
    }))

  // Pillar mastery strip
  const pillars: PillarItem[] = ['PF1', 'PF2', 'PF3', 'PF4', 'PF5'].map((code) => {
    const info = PILLAR_LABELS[code]
    const signal = knowledge?.signals?.[code as keyof typeof knowledge.signals] as any
    const mastery = Math.round(((signal?.ewmaAccuracy ?? 0) as number) * 100)
    return {
      code,
      letter: info.letter,
      label: t(`play.pillars.${code}`),
      color: info.color,
      mastery,
      spark: Array(7).fill(mastery),  // flat line for v1; history can be added later
    }
  })

  // Recent rounds — prefer email subject as the display label; fromName as subtitle
  const recentRoundsForCard: RecentRound[] = (Array.isArray(rounds) ? rounds : []).slice(0, 7).map((r: any) => {
    const rid: string = r.id ?? r.roundId ?? ''
    const completedAt = r.completedAt || r.startedAt
    // "30 Sept" in English as before; the learner's own language otherwise.
    const dateLabel = completedAt
      ? formatLearnerDate(completedAt, locale === 'en' ? 'en-GB' : locale, { day: 'numeric', month: 'short' }) || null
      : null
    const attackLabel =
      r.subject
        ? r.subject
        : r.fromName
          ? t('play.home.emailFrom', { name: r.fromName })
          : dateLabel
            ? t('play.home.roundOn', { date: dateLabel })
            : t('play.home.trainingRound')
    return {
      id: rid,
      outcome: r.outcome,
      pointsEarned: r.totalPoints ?? 0,
      stars: 0,
      timeAgo: timeAgo(completedAt, t),
      attack: attackLabel,
      pillar: Object.keys(r.phishRollup ?? {})[0] ?? '',
    }
  })

  // TIER_DEFS → local shape expected by the hero band
  const tiers = TIER_DEFS.map((t) => ({ key: t.key, name: t.name, threshold: t.threshold }))

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 space-y-5">
      {!streakBannerDismissed &&
        summary.streak.current > 0 &&
        summary.daysSinceActivity >= 6 && (
          <StreakAtRiskBanner
            streakCurrent={summary.streak.current}
            daysUntilDecay={Math.max(0, 7 - summary.daysSinceActivity)}
            playHref={playHref}
            onDismiss={() => setStreakBannerDismissed(true)}
          />
        )}

      <TierHeroBand
        userName={fullName}
        currentStars={summary.currentStars}
        currentTier={summary.currentTier}
        peakTier={summary.peakTier}
        nextTier={summary.nextTier as TierKey | null}
        starsToNextTier={summary.starsToNextTier}
        progressToNextTier={summary.progressToNextTier}
        streakCurrent={summary.streak.current}
        weekStats={summary.weekStats}
        tiers={tiers}
        howItWorksHref="/play/me"
      />

      <div className="space-y-2">
        <NextRoundCTA {...nextRoundProps} />
        <p className="text-center text-sm text-muted-foreground">
          <Link href={singlePlayHref} className="underline underline-offset-2 hover:text-foreground">
            {t('play.home.playOne')}
          </Link>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          {/* Portal players must stay inside /play/* — the card's default
              seeAllHref is the admin route /phishtrainer/leaderboard, which a
              portal-JWT learner cannot open (bounced to admin login). */}
          <LeaderboardCard
            firstName={firstName}
            scopes={leaderboardScopes}
            seeAllHref="/play/leaderboard"
          />
        </div>
        <div className="lg:col-span-2">
          <AtAGlanceCard
            progress={progress}
            streak={{ current: summary.streak.current, best: summary.streak.best }}
            pillarSlam={{ done: summary.pillarSlam.doneCodes.length, total: summary.pillarSlam.total }}
          />
        </div>
      </div>

      {/* Default seeAllHref is a dead "#" — send players to their profile page. */}
      <AchievementsRow items={achievementItems} seeAllHref="/play/me" />

      {summary.weeklyQuests && <WeeklyQuestsCard data={summary.weeklyQuests} />}

      <PillarMasteryStrip pillars={pillars} />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2">
          <FocusAndQuestsCard
            weakestPillar={weakestPillar}
            pillarSlam={{ doneCodes: summary.pillarSlam.doneCodes, total: summary.pillarSlam.total }}
            streakCurrent={summary.streak.current}
            trainHref={playHref}
          />
        </div>
        <div className="lg:col-span-3">
          {/* historyHref → the learner's full history/mastery page. Each row
              opens that round's full performance drawer IN PLACE (an overlay
              right here) via `userId`, instead of deep-linking to the Library
              rounds tab — which used to teleport the learner to another page
              just to show a drawer. */}
          <RecentRoundsCard
            rounds={recentRoundsForCard}
            historyHref="/play/me"
            userId={user?.id}
          />
        </div>
      </div>

      <AnonMerge userId={user.id} />
      <ModePreferenceCard userId={user.id} />

      {celebrateTier && (
        <TierUpCelebration newTier={celebrateTier} onDismiss={() => setCelebrateTier(null)} />
      )}
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-10 space-y-5">
      <Skeleton className="h-[400px] w-full rounded-2xl" />
      <Skeleton className="h-24 w-full rounded-2xl" />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Skeleton className="h-72 lg:col-span-3 rounded-2xl" />
        <Skeleton className="h-72 lg:col-span-2 rounded-2xl" />
      </div>
      <Skeleton className="h-40 rounded-2xl" />
      <Skeleton className="h-44 rounded-2xl" />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Skeleton className="h-60 lg:col-span-2 rounded-2xl" />
        <Skeleton className="h-60 lg:col-span-3 rounded-2xl" />
      </div>
    </div>
  )
}

function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1) }

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('')
}

function deriveWeakestPillar(
  knowledge: KnowledgeProfile | null,
  t: Translate,
): { code: string; label: string; mastery: number } {
  const fallback = { code: 'PF3', label: t('play.pillars.PF3'), mastery: 0 }
  if (!knowledge?.signals) return fallback
  const signals = knowledge.signals as Record<string, { ewmaAccuracy?: number }>
  let worst = { code: '', label: '', mastery: 101 }
  for (const code of ['PF1', 'PF2', 'PF3', 'PF4', 'PF5']) {
    const acc = Math.round((signals[code]?.ewmaAccuracy ?? 0) * 100)
    if (acc < worst.mastery) worst = { code, label: t(`play.pillars.${code}`), mastery: acc }
  }
  return worst.code ? worst : fallback
}

function computeProgressBuckets(rounds: Round[]): { reported: number; clicked: number; missed: number } {
  if (!rounds.length) return { reported: 0, clicked: 0, missed: 0 }
  let reported = 0, clicked = 0, missed = 0
  for (const r of rounds) {
    if (r.outcome === 'passed') reported++
    else if (r.outcome === 'failed') missed++
    else clicked++
  }
  return { reported, clicked, missed }
}

function timeAgo(iso: string | undefined, t: Translate): string {
  if (!iso) return ''
  const ms = Date.now() - Date.parse(iso)
  if (Number.isNaN(ms) || ms < 0) return ''
  const min = Math.round(ms / 60_000)
  if (min < 60) return t('play.time.minutesAgo', { count: min })
  const h = Math.round(min / 60)
  if (h < 24) return t('play.time.hoursAgo', { count: h })
  const d = Math.round(h / 24)
  return t('play.time.daysAgo', { count: d })
}
